/**
 * iter86.63 — NFL Standings tab (UI-only view over tournament.teams).
 *
 * AFC / NFC → four divisions each, sorted by record. Columns: W-L-T,
 * PF, PA, and Points Generated (the 3-per-win + 1-per-tie fantasy
 * points a team has earned its owners — UI-only, never stored).
 * The viewer's picked teams highlight yellow.
 */
import { useMemo, useState } from "react";
import { ListOrdered } from "lucide-react";

const CONF_TONE = {
    AFC: "text-heat border-heat bg-heat/10",
    NFC: "text-cyber border-cyber bg-cyber/10",
};

export default function NFLStandings({ tournament, myTeamIds }) {
    const [conf, setConf] = useState("AFC");
    const mySet = useMemo(() => new Set(myTeamIds || []), [myTeamIds]);
    const scoring = tournament?.scoring || {};
    const winPts = Number(scoring.win_points ?? 3);
    const tiePts = Number(scoring.draw_points ?? 1);

    const divisions = useMemo(() => {
        const byDiv = {};
        for (const tm of tournament?.teams || []) {
            if ((tm.conference || "NFC") !== conf) continue;
            const div = tm.group || "—";
            (byDiv[div] = byDiv[div] || []).push(tm);
        }
        return Object.keys(byDiv).sort().map(div => ({
            division: div,
            teams: byDiv[div].sort((a, b) =>
                (b.group_wins || 0) + 0.5 * (b.group_draws || 0)
                - ((a.group_wins || 0) + 0.5 * (a.group_draws || 0))
                || ((b.group_for || 0) - (b.group_against || 0))
                - ((a.group_for || 0) - (a.group_against || 0))
                || a.name.localeCompare(b.name)),
        }));
    }, [tournament, conf]);

    const record = (tm) => {
        const w = tm.group_wins || 0, l = tm.group_losses || 0, t = tm.group_draws || 0;
        return t ? `${w}-${l}-${t}` : `${w}-${l}`;
    };

    return (
        <div data-testid="nfl-standings" className="space-y-4">
            {/* iter87.26 — outer brutal-card + "NFL Standings" h3
                dropped (Takeover shell already ships the "Standings"
                title). Conference toggle kept, still right-aligned. */}
            <div className="flex items-center justify-end gap-2 flex-wrap">
                <div className="flex gap-1">
                    {["AFC", "NFC"].map(c => (
                        <button
                            key={c}
                            type="button"
                            data-testid={`nfl-standings-conf-${c.toLowerCase()}`}
                            onClick={() => setConf(c)}
                            className={`brutal-btn text-[10px] py-1 px-3 ${conf === c
                                ? CONF_TONE[c]
                                : "bg-transparent text-zinc-400 border-[#333] shadow-none"}`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </div>

            {divisions.length === 0 && (
                <p className="text-sm text-zinc-500 text-center py-6">
                    Standings sync once the NFL feed is connected.
                </p>
            )}

            {divisions.map(({ division, teams }) => (
                <div key={division} data-testid={`nfl-division-${division.replace(/\s+/g, "-").toLowerCase()}`}>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">{division}</p>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[9px] uppercase tracking-wider text-zinc-500 border-b border-[#222]">
                                    <th className="text-left py-1 pr-2 font-bold">Team</th>
                                    <th className="text-right py-1 px-2 font-bold">W-L-T</th>
                                    <th className="text-right py-1 px-2 font-bold hidden sm:table-cell">PF</th>
                                    <th className="text-right py-1 px-2 font-bold hidden sm:table-cell">PA</th>
                                    <th className="text-right py-1 pl-2 font-bold text-volt">Pts Gen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teams.map(tm => {
                                    const mine = mySet.has(tm.id);
                                    const gen = (tm.group_wins || 0) * winPts + (tm.group_draws || 0) * tiePts;
                                    return (
                                        <tr
                                            key={tm.id}
                                            data-testid={`nfl-standings-row-${tm.id}`}
                                            className={`border-b border-[#1a1a1a] ${mine ? "bg-cyber/10" : ""}`}
                                        >
                                            <td className="py-1.5 pr-2">
                                                <span className={`font-bold ${mine ? "text-cyber" : ""}`}>
                                                    {tm.name}
                                                </span>
                                                {tm.sb_odds && (
                                                    <span className="text-[9px] text-zinc-600 font-mono ml-1.5">{tm.sb_odds}</span>
                                                )}
                                            </td>
                                            <td className="text-right py-1.5 px-2 font-mono">{record(tm)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono text-zinc-400 hidden sm:table-cell">{tm.group_for || 0}</td>
                                            <td className="text-right py-1.5 px-2 font-mono text-zinc-400 hidden sm:table-cell">{tm.group_against || 0}</td>
                                            <td className="text-right py-1.5 pl-2 font-mono font-bold text-volt">{gen}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
}
